import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../../Environments/Environment";
import { RequestSearchRequest } from "../Dto/RequestSearchRequest";
import { RequestSearchResponse } from "../Dto/RequestSearchResponse";
import { CreateRequestRequest, UpdateRequestRequest } from "../Dto/CreateRequestRequest";
import { RequestEntity } from "../Dto/RequestModel";
import { RequestStockPreviewResponse } from "../Dto/RequestStockPreview";
import { LocationAddress } from "../../Location/Dto/LocationAddressModel";
import { sortByMaterialSequence } from "../../../Common/Utility/UtilityMaterialSequence";

@Injectable({ providedIn: "root" })
export class RequestApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.adminServiceUrl;

  async searchRequests(params: RequestSearchRequest): Promise<RequestSearchResponse> {
    const url = new URL("requests", `${this.baseUrl}/`);
    const searchParams = new URLSearchParams();

    if (params.search) searchParams.set("search", params.search);
    if (params.teamLeaderId) searchParams.set("teamLeaderId", params.teamLeaderId);
    if (params.partnerId) searchParams.set("partnerId", params.partnerId);
    if (params.warehouseId) searchParams.set("warehouseId", params.warehouseId);
    if (params.status) searchParams.set("status", params.status);
    if (params.page !== undefined) searchParams.set("page", params.page.toString());
    if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
    if (params.createdAtFrom) searchParams.set("createdAtFrom", params.createdAtFrom);
    if (params.createdAtTo) searchParams.set("createdAtTo", params.createdAtTo);

    if (searchParams.toString()) {
      url.search = searchParams.toString();
    }

    const response = await firstValueFrom(this.http.get<RequestSearchResponse>(url.toString(), { withCredentials: true }));
    return {
      ...response,
      data: response.data.map((item) => this.normalizeRequest(item)),
    };
  }

  async createRequest(payload: CreateRequestRequest): Promise<RequestEntity> {
    const url = new URL("requests", `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.post<RequestEntity>(url.toString(), payload, { withCredentials: true }));
    return this.normalizeRequest(response);
  }

  async updateRequest(id: string, payload: UpdateRequestRequest): Promise<RequestEntity> {
    const url = new URL(`requests/${id}`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.put<RequestEntity>(url.toString(), payload, { withCredentials: true }));
    return this.normalizeRequest(response);
  }

  async getRequestById(id: string): Promise<RequestEntity | null> {
    const url = new URL(`requests/${id}`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.get<RequestEntity>(url.toString(), { withCredentials: true }));
    return this.normalizeRequest(response);
  }

  async confirmRequest(id: string): Promise<RequestEntity> {
    const url = new URL(`requests/${id}/confirm`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.post<RequestEntity>(url.toString(), {}, { withCredentials: true }));
    return this.normalizeRequest(response);
  }

  async fulfillRequest(id: string): Promise<RequestEntity> {
    const url = new URL(`requests/${id}/fulfill`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.post<RequestEntity>(url.toString(), {}, { withCredentials: true }));
    return this.normalizeRequest(response);
  }

  async cancelRequest(id: string, note?: string): Promise<RequestEntity> {
    const url = new URL(`requests/${id}/cancel`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.post<RequestEntity>(url.toString(), { note }, { withCredentials: true }));
    return this.normalizeRequest(response);
  }

  async deleteRequest(id: string): Promise<void> {
    const url = new URL(`requests/${id}`, `${this.baseUrl}/`);
    await firstValueFrom(this.http.delete<void>(url.toString(), { withCredentials: true }));
  }

  async previewStock(warehouseId: string, materialIds: string[]): Promise<RequestStockPreviewResponse> {
    const url = new URL("requests/stock-preview", `${this.baseUrl}/`);
    const searchParams = new URLSearchParams();
    searchParams.set("warehouseId", warehouseId);
    materialIds.forEach((materialId) => searchParams.append("materialIds", materialId));
    url.search = searchParams.toString();

    return await firstValueFrom(this.http.get<RequestStockPreviewResponse>(url.toString(), { withCredentials: true }));
  }

  private normalizeRequest(item: RequestEntity): RequestEntity {
    const teamLeaderRecord = (() => {
      if (item.teamLeaderId && typeof item.teamLeaderId === "object") {
        return item.teamLeaderId;
      }
      return null;
    })() as {
      id?: string;
      name?: string;
      phone?: string | null;
      address?: LocationAddress | null;
    } | null;
    const nestedTeamLeader = (() => {
      if (item.teamLeader && typeof item.teamLeader === "object") {
        return item.teamLeader;
      }
      return null;
    })() as {
      id?: string;
      name?: string;
      phone?: string | null;
      address?: LocationAddress | null;
    } | null;
    const partnerRecord = (() => {
      if (item.partnerId && typeof item.partnerId === "object") {
        return item.partnerId;
      }
      return null;
    })() as {
      id?: string;
      name?: string;
      phone?: string | null;
      address?: LocationAddress | null;
    } | null;
    const nestedPartner = (() => {
      if (item.partner && typeof item.partner === "object") {
        return item.partner;
      }
      return null;
    })() as {
      id?: string;
      name?: string;
      phone?: string | null;
      address?: LocationAddress | null;
    } | null;
    const warehouseRecord = (() => {
      if (item.warehouseId && typeof item.warehouseId === "object") {
        return item.warehouseId;
      }
      return null;
    })() as {
      id?: string;
      name?: string;
    } | null;

    const normalizedItems = sortByMaterialSequence(
      (item.items ?? []).map((requestItem) => {
        const materialRecord = (() => {
          if (requestItem.materialId && typeof requestItem.materialId === "object") {
            return requestItem.materialId as { id?: string; sequence?: number; name?: string; sku?: string };
          }
          return null;
        })();

        return {
          ...requestItem,
          materialId: (() => {
            if (typeof requestItem.materialId === "string") {
              return requestItem.materialId;
            }
            return materialRecord?.id ?? "";
          })(),
          material: requestItem.material ?? {
            sequence: Number(materialRecord?.sequence ?? 0),
            name: materialRecord?.name ?? "",
            sku: materialRecord?.sku ?? "",
          },
          boxedQty: Number(requestItem.boxedQty ?? 0),
          dispatchedQty: Number(requestItem.dispatchedQty ?? 0),
          deliveredQty: Number(requestItem.deliveredQty ?? 0),
        };
      }),
      (requestItem) => requestItem.material ?? {},
    );

    return {
      ...item,
      teamLeaderId: (() => {
        if (typeof item.teamLeaderId === "string") {
          return item.teamLeaderId;
        }
        return teamLeaderRecord?.id ?? nestedTeamLeader?.id ?? "";
      })(),
      partnerId: (() => {
        if (typeof item.partnerId === "string") {
          return item.partnerId;
        }
        return partnerRecord?.id ?? nestedPartner?.id ?? "";
      })(),
      warehouseId: (() => {
        if (typeof item.warehouseId === "string") {
          return item.warehouseId;
        }
        return warehouseRecord?.id ?? "";
      })(),
      teamLeader: item.teamLeader ?? {
        id: teamLeaderRecord?.id ?? nestedTeamLeader?.id,
        name: teamLeaderRecord?.name ?? nestedTeamLeader?.name ?? "",
        phone: teamLeaderRecord?.phone ?? nestedTeamLeader?.phone ?? null,
        address: teamLeaderRecord?.address ?? nestedTeamLeader?.address ?? null,
      },
      partner: item.partner ?? {
        id: partnerRecord?.id ?? nestedPartner?.id,
        name: partnerRecord?.name ?? nestedPartner?.name ?? "",
        phone: partnerRecord?.phone ?? nestedPartner?.phone ?? null,
        address: partnerRecord?.address ?? nestedPartner?.address ?? null,
      },
      warehouse: item.warehouse ?? {
        name: warehouseRecord?.name ?? "",
      },
      items: normalizedItems,
    };
  }
}
